'use client';
import React, { useMemo, useState } from 'react';
import ISignupUser from '@/app/(auth)/signup/types/ISignupUser';
import { SignupValidator } from '@/app/(auth)/signup/components/signupStep/customerSignupValidation';
import {
  ISignupStepContext,
  SignupStepContext,
} from '@/app/(auth)/signup/components/signupStep/SignupStepContext';
import { useRouter } from 'next/navigation';
import { ISignupStepRouteResponse } from '@/app/(auth)/signup/components/signupStep/ISignupStepRouteResponse';
import { isRouteErrorResponse } from '@/utils/isRouteErrorResponse';
import { isSignupRedirect } from '@/app/(auth)/signup/components/signupStep/isSignupRedirect';
import { SignupStepId } from '@/app/(auth)/signup/SIGNUP_STEPS';
import { useMutation } from '@tanstack/react-query';
import { logrocketService } from '@/services/logrocket';

interface IClientSignupStepParams {
  children: React.ReactNode;
  initialState: ISignupUser;
  stepId: SignupStepId;
  isPassport: boolean;
  hasNext?: boolean;
  hasPrev?: boolean;
  redirectConfig: {
    baseUrl: string;
    nexudusRedirectUrl: string;
  };
  expectedFields: (keyof ISignupUser)[];
}

export function ClientSignupStep(props: IClientSignupStepParams) {
  const { children, initialState, hasNext, hasPrev } = props;
  const [details, setDetails] = useState<ISignupUser>(initialState);
  const router = useRouter();

  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string[]>>({});

  const [isRedirecting, setIsRedirecting] = useState(false);

  // Submits the singup action back to the server. This is called for both next and previous steps.
  const submitAction = async ({
    extras,
    action,
  }: {
    extras?: Record<string, string>;
    action: 'prev' | 'next';
  }) => {
    const url = `${process.env.NEXT_PUBLIC_AUTH_BASE_URL}/signup/submit`;

    const searchParams = new URLSearchParams();
    searchParams.set('action', action);
    searchParams.set('step', props.stepId);

    const response = await fetch(`${url}?${searchParams.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        extras: extras,
        signup: details,
      }),
    });

    const body = (await response.json()) as ISignupStepRouteResponse;
    if (isRouteErrorResponse(body)) {
      setServerFieldErrors(body.fieldErrors || {});
      const error = new Error(body.error);
      logrocketService.logException(error);
      throw error;
    }
    if (isSignupRedirect(body)) {
      setIsRedirecting(true);
      router.push(body.redirect);
      router.refresh();
    }
  };

  const submitMutation = useMutation({
    mutationFn: submitAction,
    mutationKey: [props.stepId, 'submit'],
  });

  // Calculate and cache the functions to transition to the next and previous steps
  const nextStep = useMemo(() => {
    if (hasNext) {
      return (extras?: Record<string, string>) =>
        submitMutation.mutate({
          extras,
          action: 'next',
        });
    } else {
      return undefined;
    }
  }, [submitAction, hasNext]);

  const prevStep = useMemo(() => {
    if (hasPrev) {
      return () => submitMutation.mutate({ extras: {}, action: 'prev' });
    } else {
      return undefined;
    }
  }, [submitAction, hasPrev]);

  const validationValue = useMemo(() => {
    const validator = new SignupValidator(details);
    return validator.validate(props.expectedFields);
  }, [details, props.expectedFields]);

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string[]> = {};
    for (const [field, errorsFromValidation] of Object.entries(validationValue.errors)) {
      errors[field] = errorsFromValidation;
    }

    for (const [field, errorsForField] of Object.entries(serverFieldErrors)) {
      errors[field] = [...(errors[field] || []), ...errorsForField];
    }

    return errors;
  }, [validationValue, serverFieldErrors]);

  const isSubmitting = submitMutation.isPending || isRedirecting;

  const contextValue = useMemo<ISignupStepContext>(
    () => ({
      next: nextStep,
      prev: prevStep,
      details,
      setDetails,
      isSubmittingNext: isSubmitting && submitMutation.variables?.action === 'next',
      isSubmittingPrev: isSubmitting && submitMutation.variables?.action === 'prev',
      isPassport: props.isPassport,
      isPresent: validationValue.isPresent,
      isValid: validationValue.isValid,
      fieldErrors: fieldErrors,
      error: submitMutation.error ? submitMutation.error.message : undefined,
      fieldValidity: validationValue.fieldValidity,
      stepId: props.stepId,
      redirectConfig: props.redirectConfig,
    }),
    [
      details,
      submitMutation.isPending,
      submitMutation.variables?.action,
      submitMutation.error,
      validationValue,
      nextStep,
      prevStep,
    ],
  );

  return <SignupStepContext.Provider value={contextValue}>{children}</SignupStepContext.Provider>;
}
